  import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
  import { updateProfile } from 'firebase/auth';
  import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc, writeBatch  } from 'firebase/firestore';
  import { auth, db } from '../../firebase/firebase';

  const getFullName = (firstname, lastname) =>
    [firstname, lastname].filter(Boolean).join(' ').trim();
  export const syncPhotoAcrossOrders = createAsyncThunk(
    'profiles/syncPhotoAcrossOrders',
    async ({ uid, photoURL }) => {
      const batch = writeBatch(db);
      let updateCount = 0;

      const [buyerSnap, sellerSnap] = await Promise.all([
        getDocs(query(collection(db, 'orders'), where('buyerUid', '==', uid))),
        getDocs(query(collection(db, 'orders'), where('sellerUid', '==', uid)))
      ]);

      buyerSnap.docs.forEach((orderDoc) => {
        batch.update(doc(db, 'orders', orderDoc.id), { buyerPhotoURL: photoURL });
        updateCount++;
      });
      sellerSnap.docs.forEach((orderDoc) => {
        batch.update(doc(db, 'orders', orderDoc.id), { sellerPhotoURL: photoURL });
        updateCount++;
      });

      if (updateCount > 0) await batch.commit();
      return { photoURL };
    }
  );

  /** modified this function so that when userProfile is updated, also content of "userdata" is updated */
  export const addOrUpdateProfileForm = createAsyncThunk(
    'profiles/addOrUpdateProfileForm',
    async ({ uid, data }, { rejectWithValue }) => {
      try {
        const profileDocRef = doc(db, 'users', uid, 'data', 'profile');
        await setDoc(profileDocRef, { ...data }, { merge: true });

        const nameChanged = data.firstname !== undefined || data.lastname !== undefined;
        const photoChanged = !!data.url;
        const newName = getFullName(data.firstname, data.lastname);
        const userDataUpdates = {};

        if (data.url) {
          userDataUpdates.photoURL = data.url;
        }

        if (data.firstname !== undefined) {
          userDataUpdates.firstname = data.firstname;
        }

        if (data.lastname !== undefined) {
          userDataUpdates.lastname = data.lastname;
        }

        if (nameChanged && newName) {
          userDataUpdates.displayName = newName;
        }

        if (Object.keys(userDataUpdates).length > 0) {
          await updateDoc(doc(db, 'users', uid, 'data', 'userdata'), userDataUpdates);
        }

        if (nameChanged && newName && auth.currentUser?.uid === uid) {
          await updateProfile(auth.currentUser, { displayName: newName });
        }

        // Update buyerName/sellerName/photo in all orders when user changes name or photo
        if (nameChanged || photoChanged) {
          const batch = writeBatch(db);
          let updateCount = 0;

          // Update orders where user is BUYER
          const buyerOrdersQuery = query(
            collection(db, 'orders'),
            where('buyerUid', '==', uid)
          );
          const sellerOrdersQuery = query(
            collection(db, 'orders'),
            where('sellerUid', '==', uid)
          );

          const [buyerOrdersSnap, sellerOrdersSnap, myChatsSnap] = await Promise.all([
            getDocs(buyerOrdersQuery),
            getDocs(sellerOrdersQuery),
            getDocs(collection(db, 'users', uid, 'chats'))
          ]);

          buyerOrdersSnap.docs.forEach((orderDoc) => {
            const update = {};
            if (nameChanged) update.buyerName = newName;
            if (photoChanged) update.buyerPhotoURL = data.url || '';
            batch.update(doc(db, 'orders', orderDoc.id), update);
            updateCount++;
          });

          sellerOrdersSnap.docs.forEach((orderDoc) => {
            const update = {};
            if (nameChanged) update.sellerName = newName;
            if (photoChanged) update.sellerPhotoURL = data.url || '';
            batch.update(doc(db, 'orders', orderDoc.id), update);
            updateCount++;
          });

          if (updateCount > 0) {
            await batch.commit();
          }

          // Update the name shown in each contact's chat list entry for this user.
          // users/{uid}/chats lists who this user talks to; each contact also has
          // users/{contactUid}/chats/{uid} — that's the entry showing this user's name.
          const chatBatch = writeBatch(db);
          myChatsSnap.docs.forEach((chatDoc) => {
            const contactUid = chatDoc.data().contactUid;
            if (contactUid) {
              chatBatch.update(doc(db, 'users', contactUid, 'chats', uid), { name: newName });
            }
          });
          await chatBatch.commit();
        }
        return data;
        
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  export const toggleProfileActive = createAsyncThunk(
    'profiles/toggleProfileActive',
    async ({ uid, isActive }, { rejectWithValue }) => {
      try {
        // 1. Update profileIsActive in the user's profile document
        const profileRef = doc(db, 'users', uid, 'data', 'profile');
        await setDoc(profileRef, { profileIsActive: isActive }, { merge: true });

        // 2. Update jobs of this user
        const jobsRef = collection(db, 'jobs');
        const q = query(jobsRef, where('uid', '==', uid));
        const snap = await getDocs(q);

        const updatePromises = snap.docs.map((docSnap) => {
          const jobData = docSnap.data();
          const currentStatus = jobData.status;
          const previousStatus = jobData.previousStatus;

          if (isActive) {
            // Restore previous status (active or paused)
            if (currentStatus === 'inactive' && previousStatus) {
              return updateDoc(doc(db, 'jobs', docSnap.id), { 
                status: previousStatus,
                previousStatus: null  // Clear previousStatus
              });
            } else if (currentStatus === 'inactive') {
              // No previousStatus saved, default to active
              return updateDoc(doc(db, 'jobs', docSnap.id), { status: 'active' });
            }
          } else {
            // Save current status before setting to inactive
            if (currentStatus !== 'inactive') {
              return updateDoc(doc(db, 'jobs', docSnap.id), { 
                status: 'inactive',
                previousStatus: currentStatus  // Save paused or active
              });
            }
          }
          return Promise.resolve();
        });

        await Promise.all(updatePromises);

        return { uid, isActive };
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  export const getUserProfile = createAsyncThunk(
    'profiles/getUserProfile',
    async (uid, { rejectWithValue }) => {
      try {
        const userSnap = await getDoc(doc(db, 'users', uid, 'data', 'userdata'));
        const profileSnap = await getDoc(doc(db, 'users', uid, 'data', 'profile'));
        const companiesSnap = await getDoc(doc(db, 'users', uid, 'data', 'companies'));

        const ud = userSnap.exists() ? userSnap.data() : {};
        // address may be saved as a nested object {street,city,postalCode,country} from registration
        const addrObj = (ud.address && typeof ud.address === 'object') ? ud.address : {};

        // Parse dateOfBirth string ("YYYY-MM-DD") into the separate day/month/year fields Step1.jsx uses
        let dobDate = '', dobMonth = '', dobYear = '';
        if (ud.dateOfBirth) {
          // Could be a string "YYYY-MM-DD" or a Firestore Timestamp
          const raw = ud.dateOfBirth?.toDate ? ud.dateOfBirth.toDate() : new Date(ud.dateOfBirth);
          if (!isNaN(raw.getTime())) {
            dobDate = raw.getUTCDate();
            dobMonth = raw.getUTCMonth() + 1;
            dobYear = raw.getUTCFullYear();
          }
        }

        const defaultProfile = {
          created: ud.created || null,
          lastSeen: ud.lastseen || null,
          userType: ud.userType || 'buyer',
          profileIsActive: false,
          userVatNumber: ud.userVatNumber || null,
          paymentMethods: [],
          // personal info from registration — pre-fills Step1
          firstname: ud.firstname || '',
          lastname: ud.lastname || '',
          languages: ud.languages || [],
          // date of birth split into day/month/year for Step1.jsx dropdowns
          date: dobDate,
          month: dobMonth,
          year: dobYear,
          // flatten address object to the flat fields Step1.jsx expects
          address: addrObj.street || (typeof ud.address === 'string' ? ud.address : '') || '',
          city: addrObj.city || ud.city || '',
          postalCode: addrObj.postalCode || ud.postalCode || '',
          country: addrObj.country || ud.country || '',
        };

        const companies = companiesSnap.exists() ? (companiesSnap.data().companies || []) : [];

        if (profileSnap.exists()) {
          return {
            ...defaultProfile,
            ...profileSnap.data(),
            companies
          };
        } else {
          return { ...defaultProfile, companies };
        }
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  export const getSellerProfile = createAsyncThunk('profiles/getSellerProfile', async (uid, { rejectWithValue }) => {
    try {
      const userSnap    = await getDoc(doc(db, 'users', uid, 'data', 'userdata'));
      const profileSnap = await getDoc(doc(db, 'users', uid, 'data', 'profile'));

      const ud = userSnap.exists() ? userSnap.data() : {};

      const fromUserdata = {
        email:          ud.email,
        created:        ud.created,
        lastSeen:       ud.lastseen,
        userType:       ud.userType,
        sellerTotalRating: ud.sellerTotalRating || 0,
        sellerTotalAmount: ud.sellerTotalAmount || 0,
        buyerTotalRating:  ud.buyerTotalRating  || 0,
        buyerTotalAmount:  ud.buyerTotalAmount  || 0,
        userVatNumber:  ud.userVatNumber,
        paymentMethods: [],
        firstname:      ud.firstname    || '',
        lastname:       ud.lastname     || '',
        languages:      ud.languages    || [],
        photoURL:       ud.photoURL     || '',
      };

      if (profileSnap.exists()) {
        return { ...fromUserdata, ...profileSnap.data() };
      } else {
        return fromUserdata;
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  });

  export const saveCompany = createAsyncThunk(
    'profiles/saveCompany',
    async ({ uid, company }, { getState, rejectWithValue }) => {
      try {
        const companies = getState().profile.userProfile.companies || [];
        const exists = companies.find((c) => c.id === company.id);
        const updated = exists
          ? companies.map((c) => (c.id === company.id ? company : c))
          : [...companies, company];
        await setDoc(doc(db, 'users', uid, 'data', 'companies'), { companies: updated });
        return updated;
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  export const deleteCompany = createAsyncThunk(
    'profiles/deleteCompany',
    async ({ uid, companyId }, { getState, rejectWithValue }) => {
      try {
        const companies = (getState().profile.userProfile.companies || []).filter(
          (c) => c.id !== companyId
        );
        await setDoc(doc(db, 'users', uid, 'data', 'companies'), { companies });
        return companies;
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  export const toggleReviewVisibility = createAsyncThunk(
    'profiles/toggleReviewVisibility',
    async ({ uid, field, value }, { rejectWithValue }) => {
      try {
        await setDoc(doc(db, 'users', uid, 'data', 'profile'), { [field]: value }, { merge: true });
        return { field, value };
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  const initialState = {
    userProfile: {},
    sellerProfile: {}
  };

  export const profileSlice = createSlice({
    name: 'profiles',
    initialState: initialState,
    reducers: {
      updateSteps(state, action) {
        state.userProfile = {
          ...state.userProfile,
          ...action.payload
        };
      },
      resetProfileForm() {
        return initialState;
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(addOrUpdateProfileForm.fulfilled, (state, action) => {
          state.userProfile = {
            ...state.userProfile,
            ...action.payload
          };
        })
        .addCase(getUserProfile.fulfilled, (state, action) => {
          state.userProfile = action.payload;
        })
        .addCase(getUserProfile.rejected, (state, action) => {
          console.error('Error fetching user profile:', action.error);
        })
        .addCase(getSellerProfile.fulfilled, (state, action) => {
          return {
            ...state,
            sellerProfile: action.payload
          };
        })
        .addCase(toggleProfileActive.fulfilled, (state, action) => {
          state.userProfile = {
            ...state.userProfile,
            profileIsActive: action.payload.isActive
          };
        })
        .addCase(saveCompany.fulfilled, (state, action) => {
          state.userProfile = { ...state.userProfile, companies: action.payload };
        })
        .addCase(deleteCompany.fulfilled, (state, action) => {
          state.userProfile = { ...state.userProfile, companies: action.payload };
        })
        .addCase(toggleReviewVisibility.fulfilled, (state, action) => {
          state.sellerProfile = {
            ...state.sellerProfile,
            [action.payload.field]: action.payload.value
          };
          state.userProfile = {
            ...state.userProfile,
            [action.payload.field]: action.payload.value
          };
        });
    }
  });

  export const { updateSteps, resetProfileForm } = profileSlice.actions;
  export default profileSlice.reducer;
